import { isEmpty } from 'lodash';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import MainCard from 'components/MainCard';
import { useEffect, useState } from 'react';
import InputLabel from 'components/InputLabel/index';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Chip, Divider, Skeleton } from '@mui/material';
import { filterChildOptionLabel } from 'pages/globals/global';
import comptroller from 'assets/images/comptroller_white.svg';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import CustomTextInput from 'components/inputs/CustomTextInput';
import DetailPageHeader from 'components/header/DetailPageHeader';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { Button, Col, Form, Input, InputNumber, Row, Select, Spin } from 'antd';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetLynFormDataMutation } from 'store/features/comptroller/comptrollerApi';
import {
	useGetSingleIdentityMutation,
	useGetLynFormPropertyMutation,
	useGetControlQuestionnaireByIdMutation,
} from 'store/features/process/processApi';

export const assurerAndControlOwnerOptions = [
	{ key: 1, label: 'Position', value: 'DATA_positionList' },
	{ key: 2, label: 'Process Role', value: 'DATA_processRoleList' },
	{ key: 3, label: 'PGR', value: 'DATA_PGRList' },
];

function CreateV2() {
	const [form] = Form.useForm();

	const { state } = useLocation();
	const navigate = useNavigate();

	const [submitData, setSubmitData] = useState();
	const [showModal, setShowModal] = useState(false);
	const [selectedQuestionnaireData, setSelectedQuestionnaireData] = useState(null);

	const [GetSingleIdentity, { error: errorData, isSuccess: isSingleCaseSuccess, isLoading: isSingleCaseLoading, isError: isSingleCaseError }] =
		useGetSingleIdentityMutation();
	const [GetControlQuestionnaireById, { data: ControlQuestionnaire }] = useGetControlQuestionnaireByIdMutation();

	const onSelectHandler = (controlId) => {
		const payload = {
			data: controlId,
			scriptName: 'getQuestionnaire',
			processCode: state?.processCode,
		};

		GetControlQuestionnaireById(payload);
	};

	const onSubmitHandler = (payload) => {
		setSubmitData(payload);
		setShowModal(true);
	};

	const onModalOkHandler = () => {
		GetSingleIdentity({
			suffixCode: '',
			data: submitData,
			operation: 'add-single',
			processCode: state?.processCode,
		});
	};

	useEffect(() => {
		if (ControlQuestionnaire?.responseData) {
			const questionnaire = ControlQuestionnaire?.responseData;

			setSelectedQuestionnaireData({
				controlQuestion: questionnaire?.controlQuestion,
			});

			form.setFieldValue('ownerIdentityValue', questionnaire?.ownerFullName);
		}
	}, [ControlQuestionnaire]);

	useEffect(() => {
		if (isSingleCaseSuccess) {
			toast.success('Success! Action completed ', toastConfig);

			navigate(-1);
		}

		if (isSingleCaseError) {
			if (errorData?.data?.code === '501') {
				toast.error(errorData?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isSingleCaseSuccess, isSingleCaseError, errorData]);

	useEffect(() => {
		if (isEmpty(state?.processCode)) {
			navigate(-1);
		}
	}, [state?.processCode]);

	return (
		<Box>
			<DetailPageHeader title={'Assign Control for Self Assurance' ?? ''} />

			<Spin spinning={isSingleCaseLoading} style={{ maxHeight: '100%' }}>
				<MainCard>
					<Form form={form} layout="vertical" onFinish={onSubmitHandler} autoComplete="off" requiredMark={false}>
						<AntdValuePreview form={form} show={false} />

						<Row gutter={[16, 0]}>
							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Control Domain" head={true} />
							</Col>
							<Col xs={24} md={18} lg={18} xl={8}>
								<NonDependantSelect
									name="DATA_controlDomain"
									code="DATA_controlDomain"
									displayNameCode="DATA_controlDomain_label"
									onSelectHandlerFunc={() => form.setFieldValue('controlId')}
								/>
							</Col>

							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Control Sub-Domain" head={true} />
							</Col>
							<Col xs={24} md={18} lg={18} xl={8}>
								<NonDependantSelect
									name="DATA_controlSubDomain"
									code="DATA_controlSubDomain"
									displayNameCode="DATA_controlSubDomain_label"
									onSelectHandlerFunc={() => form.setFieldValue('controlId')}
								/>
							</Col>

							<Col xs={24} md={4}>
								<InputLabel label="Control ID" head={true} />
							</Col>
							<Col xs={24} md={18} lg={6} xl={8}>
								<Form.Item
									noStyle
									shouldUpdate={(previousValue, currentValue) =>
										previousValue?.DATA_controlDomain !== currentValue?.DATA_controlDomain ||
										previousValue?.DATA_controlSubDomain !== currentValue?.DATA_controlSubDomain
									}
								>
									{({ getFieldValue }) => (
										<MultiDependantSelect
											name="controlId"
											isDependent={true}
											displayNameCode="controlIdLabel"
											onSelectHandlerFunc={onSelectHandler}
											dependencyKeys={['controlDomain', 'controlSubDomain']}
											dependencyValues={[getFieldValue('DATA_controlDomain'), getFieldValue('DATA_controlSubDomain')]}
											rules={[
												{
													required: true,
													message: 'Field is required',
												},
											]}
										/>
									)}
								</Form.Item>
							</Col>

							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Control Owner" head={true} />
							</Col>
							<Col xs={24} md={18} lg={6} xl={8}>
								<CustomTextInput form={form} name="ownerIdentityValue" disabled={true} />
							</Col>

							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Control Questionnaire" head={true} />
							</Col>
							<Col xs={24} md={18} lg={18} xl={20}>
								<Chip
									variant="outlined"
									label={selectedQuestionnaireData?.controlQuestion}
									sx={{
										width: '100%',
										fontWeight: 600,
										marginBottom: '24px',
										background: '#f2f2f2',
										justifyContent: 'start',
										borderColor: '#e2ebf7',
									}}
								/>
							</Col>

							<Col xs={24}>
								<Divider sx={{ mb: 3 }} />
							</Col>

							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Assurer - by Identity" head={true} />
							</Col>
							<Col xs={24} md={18} lg={6} xl={8}>
								<Form.Item
									name="assurerIdentityType"
									rules={[
										{
											required: true,
											message: 'Field is required',
										},
									]}
								>
									<Select
										showSearch
										allowClear
										filterOption={true}
										style={{ width: '100%' }}
										optionFilterProp="children"
										placeholder="Select from the list"
										onChange={() => form.setFieldValue('assurerIdentityValue')}
									>
										{assurerAndControlOwnerOptions?.map(({ key, value, label }) => (
											<Select.Option key={key} value={value}>
												{label}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Assurer - Identity Value" head={true} />
							</Col>
							<Col xs={24} md={18} lg={6} xl={8}>
								<Form.Item
									noStyle
									shouldUpdate={(previousValue, currentValue) =>
										previousValue?.assurerIdentityType !== currentValue?.assurerIdentityType
									}
								>
									{({ getFieldValue }) => (
										<DependantSelect
											isDependent={true}
											name="assurerIdentityValue"
											displayNameCode="assurerIdentityLabel"
											code={getFieldValue('assurerIdentityType')}
											rules={[
												{
													required: true,
													message: 'Field is required',
												},
											]}
										/>
									)}
								</Form.Item>
							</Col>

							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Reporting Frequency" head={true} />
							</Col>
							<Col xs={24} md={18} lg={18} xl={8}>
								<NonDependantSelect name="PROPERTY_frequency" code="PROPERTY_frequency" displayNameCode="PROPERTY_frequency_label" />
							</Col>

							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Reporting Timeline" head={true} />
							</Col>
							<Col xs={24} md={18} lg={18} xl={8}>
								<Form.Item
									name="deadlineDaysCount"
									rules={[
										{
											required: true,
											message: 'Field is required',
										},
									]}
								>
									<InputNumber
										min={1}
										max={31}
										style={{ width: '100%' }}
										placeholder="n'th instant of the following month (5, 10, 15 etc.)"
									/>
								</Form.Item>
							</Col>

							<Col xs={24} md={6} lg={6} xl={4}>
								<InputLabel label="Expectation" head={true} />
							</Col>
							<Col xs={24} md={18} lg={18} xl={8}>
								<Form.Item
									name="expectation"
									placeholder="Write here"
									rules={[
										{
											required: true,
											message: 'Field is required',
										},
									]}
								>
									<Input />
								</Form.Item>
							</Col>

							<Col xs={24}>
								<Button type="primary" htmlType="submit">
									Submit
								</Button>
							</Col>
						</Row>
					</Form>
				</MainCard>
			</Spin>

			{showModal && (
				<ComptrollerConfirmModal
					showModal={showModal}
					setShowModal={setShowModal}
					handleSubmit={onModalOkHandler}
					modalData={{
						head: state?.processName ?? '',
						label: 'Do you want to proceed?',
					}}
				/>
			)}
		</Box>
	);
}

export default CreateV2;

export const MultiDependantSelect = ({
	name,
	rules,
	label = '',
	displayNameCode,
	onClear = () => {},
	isDependent = false,
	dependencyKeys = [],
	onSelectHandlerFunc,
	dependencyValues = [],
}) => {
	const form = Form.useFormInstance();

	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const [options, setOptions] = useState(isDependent ? [] : null);

	const [getQueryV3, { data: queryV3Data }] = useGetQueryV3Mutation();

	const onSelectHandler = (value, obj) => {
		if ((displayNameCode, obj?.children)) {
			form.setFieldValue(displayNameCode, obj?.children);
		}

		if (typeof onSelectHandlerFunc === 'function') onSelectHandlerFunc(value);
	};

	useEffect(() => {
		const isValid = dependencyKeys.every((key, index) => {
			const value = dependencyValues[index];

			return !isEmpty(value);
		});

		if (companyCode && isValid) {
			const payload = {
				companyCode,
				name: 'ControlQuestionnaireList-v2',
				fieldCodes: ['companyCode', ...dependencyKeys],
				fieldValues: [companyCode, ...dependencyValues],
			};

			getQueryV3(payload);
		}
	}, [...dependencyValues]);

	useEffect(() => {
		if (queryV3Data?.results?.length > 0) {
			setOptions(queryV3Data?.results);
		} else {
			setOptions([]);
		}
	}, [queryV3Data]);

	return (
		<>
			<Form.Item name={name} rules={rules} label={label}>
				{options === null ? (
					<Skeleton variant="text" sx={{ fontSize: '1rem', marginX: 1 }} />
				) : (
					<Select
						allowClear
						showSearch
						onClear={onClear}
						onSelect={onSelectHandler}
						placeholder="Select from the list"
						filterOption={filterChildOptionLabel}
						style={{ height: '32px', borderRadius: '4px', width: '100%' }}
					>
						{options?.map((value, index) => (
							<Select.Option key={index} value={value?.propertyCode}>
								{value?.displayName}
							</Select.Option>
						))}
					</Select>
				)}
			</Form.Item>
			<Form.Item noStyle name={displayNameCode} />
		</>
	);
};

export const DependantSelect = ({ name, label = '', displayNameCode, code, rules, isDependent = false, processCode }) => {
	const { state } = useLocation();
	const form = Form.useFormInstance();

	const [options, setOptions] = useState(isDependent ? [] : null);

	const userId = useSelector((state) => state.profile.profileData.userId);
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const [GetLynFormData, { data: lynFormData }] = useGetLynFormDataMutation();
	const [GetLynFormProperty, { data: lynProperty }] = useGetLynFormPropertyMutation();

	const onSelectHandler = (value) => {
		if (displayNameCode) {
			const label = form.getFieldValue(value);
			form.setFieldValue(displayNameCode, label);
		}
	};

	useEffect(() => {
		if (code?.startsWith('PROPERTY_')) {
			GetLynFormProperty({
				type: 'FORM',
				companyCode,
				status: 'Active',
				propertyCode: code,
				processVersion: state?.processVersion,
				fieldCode: state?.processCode || processCode,
			});
		} else if (code?.startsWith('DATA_')) {
			const queryName = code.replace('DATA_', '');

			GetLynFormData({
				name: `${queryName}`,
				userId,
			});
		}
	}, [code, userId]);

	useEffect(() => {
		if (lynProperty?.lynProperties?.length > 0) {
			setOptions(lynProperty?.lynProperties);

			lynProperty?.lynProperties?.forEach((value) => {
				form.setFieldsValue({
					[value?.propertyValue]: value?.displayName,
				});
			});
		}

		if (lynFormData?.results?.length > 0) {
			setOptions(lynFormData?.results);

			lynFormData?.results?.forEach((value) => {
				form.setFieldsValue({
					[value?.propertyValue]: value?.displayName,
				});
			});
		}
	}, [lynProperty, lynFormData]);

	return (
		<>
			<Form.Item name={name} rules={rules} label={label}>
				{options === null ? (
					<Skeleton variant="text" sx={{ fontSize: '1rem', marginX: 1 }} />
				) : (
					<Select
						allowClear
						showSearch
						autoComplete="off"
						onSelect={onSelectHandler}
						placeholder="Select from the list"
						filterOption={filterChildOptionLabel}
						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
					>
						{options?.map((value, index) => (
							<Select.Option key={index} value={value?.propertyValue}>
								{value?.displayName}
							</Select.Option>
						))}
					</Select>
				)}
			</Form.Item>
			<Form.Item noStyle name={displayNameCode} />
		</>
	);
};

export const NonDependantSelect = ({ name, label = '', code, displayNameCode, processCode, onSelectHandlerFunc }) => {
	const { state } = useLocation();
	const form = Form.useFormInstance();

	const [options, setOptions] = useState(null);

	const userId = useSelector((state) => state.profile.profileData.userId);
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const [GetLynFormData, { data: lynFormData }] = useGetLynFormDataMutation();
	const [GetLynFormProperty, { data: lynProperty }] = useGetLynFormPropertyMutation();

	const onSelectHandler = (value, obj) => {
		if (displayNameCode && obj?.children) {
			form.setFieldValue(displayNameCode, obj?.children);
		}

		if (typeof onSelectHandlerFunc === 'function') onSelectHandlerFunc();
	};

	useEffect(() => {
		if (code?.startsWith('PROPERTY_')) {
			GetLynFormProperty({
				type: 'FORM',
				companyCode,
				status: 'Active',
				propertyCode: code,
				processVersion: state?.processVersion,
				fieldCode: state?.processCode || processCode,
			});
		} else if (code?.startsWith('DATA_')) {
			const queryName = code.replace('DATA_', '');

			GetLynFormData({
				name: `${queryName}-v2`,
				userId,
			});
		}
	}, [code, userId]);

	useEffect(() => {
		if (lynProperty?.lynProperties?.length > 0) {
			setOptions(lynProperty?.lynProperties);
		}

		if (lynFormData?.results?.length > 0) {
			setOptions(lynFormData?.results);
		}
	}, [lynProperty, lynFormData]);

	return (
		<>
			<Form.Item
				name={name}
				label={label}
				rules={[
					{
						required: true,
						message: 'Field is required',
					},
				]}
			>
				<Select
					allowClear
					showSearch
					onSelect={onSelectHandler}
					placeholder="Select from the list"
					filterOption={filterChildOptionLabel}
					style={{ width: '100%', height: '32px', borderRadius: '4px', bgcolor: '#f2f2f2' }}
				>
					{options === null ? (
						<Skeleton variant="text" sx={{ fontSize: '1rem', marginX: 1 }} />
					) : (
						options?.map((value, index) => (
							<Select.Option key={index} value={value?.propertyValue}>
								{value?.displayName}
							</Select.Option>
						))
					)}
				</Select>
			</Form.Item>
			<Form.Item noStyle name={displayNameCode} />
		</>
	);
};
